import {
	type Dispatch,
	type SetStateAction,
	useCallback,
	useEffect,
} from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import NodeController05Abi from "@abi/NodeController05";
import config from "@config";
import useContract from "@hooks/useContract";
import useToast from "@hooks/useToast";
import type { MyLocale } from "@i18n";
import { useI18n } from "next-rosetta";
import type { Address } from "viem";
import { useSimulateContract, useWriteContract } from "wagmi";

const useCompound = (nodeIDs: bigint[]) => {
	const { nodeController05Contract } = useContract();
	const { data } = useSimulateContract({
		abi: NodeController05Abi,
		address: nodeController05Contract.address as Address,
		args: [nodeIDs],
		functionName: "compound",
		gas: BigInt("1500000"),
	});

	const {
		isPending: isLoading,
		reset,
		status,
		writeContract,
	} = useWriteContract();
	const state = status;
	const write = useCallback(() => {
		if (data?.request) {
			return writeContract(data?.request);
		}
	}, [data]);
	return { isLoading, resetState: reset, send: write, state, status };
};

export default function CompoundAll({
	nodeIDs,
	open,
	setOpen,
}: {
	open: boolean;
	nodeIDs: bigint[];
	setOpen: Dispatch<SetStateAction<boolean>>;
}) {
	const i18n = useI18n<MyLocale>();
	const { t } = i18n;
	const { isLoading, send: compound, state, status } = useCompound(nodeIDs);
	const handleCompound = () => compound();
	const handleClose = useCallback(() => setOpen(false), [setOpen]);
	const { contractFunctionToast } = useToast();

	useEffect(() => {
		contractFunctionToast("Compound", state);
	}, [state, contractFunctionToast]);

	useEffect(() => {
		if (status && status === "success") {
			handleClose();
		}
	}, [status, handleClose]);

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			PaperProps={{
				sx: {
					backgroundImage: "unset",
					borderRadius: 5,
				},
			}}
		>
			<DialogTitle>{t("modals.compoundAll.compoundAll")}</DialogTitle>
			<DialogContent sx={{ minWidth: 280 }}>
				{t("modals.compoundAll.content", {
					compoundFee: config.appTokenomics.compoundFee,
				})}
			</DialogContent>
			<DialogActions
				sx={{
					justifyContent: "space-between",
					paddingBottom: 4,
					px: 4,
				}}
			>
				<Button onClick={handleClose} variant="outlined" color="error">
					{t("modals.compoundAll.cancel")}
				</Button>

				<LoadingButton
					variant="contained"
					color="primary"
					onClick={handleCompound}
					loading={isLoading}
				>
					{t("modals.compoundAll.compoundAll")}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
}
